import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { PROPERTY_TYPES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PropertyCard from "@/components/PropertyCard";
import LocationPicker from "@/components/LocationPicker";
import HeroCarousel from "@/components/HeroCarousel";
import { api } from "@/lib/api";
import { Search, Sparkles, Handshake, MapPinned, Landmark, ArrowRight, ArrowLeft } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";

export default function Home() {
  const { t, lang, isRTL } = useLang();
  const { content } = useSiteContent();
  const heroTitle = content[`hero_title_${lang}`] || t.hero.h1;
  const heroSub = content[`hero_sub_${lang}`] || t.hero.sub;
  const launchMsg = content[`launch_message_${lang}`] || t.hero.freeLaunch;
  const launchActive = content.launch_active !== false;
  const nav = useNavigate();
  const [latest, setLatest] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [f, setF] = useState({ type: "", city: "", area: "", price_min: "", price_max: "" });
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    api.get("/properties?limit=8").then((r) => setLatest(r.data.items || [])).catch(() => {});
    api.get("/properties/featured?limit=6").then((r) => setFeatured(r.data.items || [])).catch(() => {});
  }, []);

  const doSearch = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    nav(`/properties?${p.toString()}`);
  };

  return (
    <div>
      {/* HERO - Premium AI carousel banner */}
      <HeroCarousel />

      {/* SEARCH SECTION */}
      <section className="fl-hero-light relative overflow-hidden">
        <div className="fl-topo absolute inset-0 opacity-70" />
        <div className="max-w-7xl mx-auto px-6 py-10 sm:py-14 relative">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-amber-800 border border-amber-300/70 bg-amber-50/60 px-3 py-1.5 rounded-full mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {t.home.launchTitle}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] text-emerald-950">
              {heroTitle}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-700">{heroSub}</p>
          </div>

          {/* Search card */}
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-4 shadow-[0_30px_80px_-40px_rgba(6,44,34,.35)] border border-emerald-900/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="sm:col-span-2 lg:col-span-3">
              <LocationPicker
                city={f.city} area={f.area}
                onChange={(loc) => setF({ ...f, ...loc })}
              />
            </div>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="hero-type" className="h-11"><SelectValue placeholder={t.filters.type} /></SelectTrigger>
              <SelectContent>{PROPERTY_TYPES.map((x) => <SelectItem key={x.key} value={x.key}>{lang === "ar" ? x.ar : x.en}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="hero-price-min" type="number" placeholder={t.filters.priceFrom} className="h-11" value={f.price_min} onChange={(e) => setF({ ...f, price_min: e.target.value })} />
            <Input data-testid="hero-price-max" type="number" placeholder={t.filters.priceTo} className="h-11" value={f.price_max} onChange={(e) => setF({ ...f, price_max: e.target.value })} />
            <Button data-testid="hero-search-btn" onClick={doSearch} className="sm:col-span-2 lg:col-span-3 h-11 bg-emerald-900 hover:bg-emerald-950 text-white gap-2">
              <Search className="w-4 h-4" /> {t.hero.search}
            </Button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <Button data-testid="hero-list-cta" onClick={() => nav("/sell")} className="bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold h-12 px-6">
              {t.hero.listCta}
            </Button>
            <div className="text-xs text-slate-600">
              {launchActive && <div className="font-semibold text-emerald-900">{launchMsg}</div>}
              <div>{t.hero.youSetPrice}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-emerald-950 mb-8 fl-gold-underline">{t.home.categories}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {PROPERTY_TYPES.map((c) => (
            <Link key={c.key} to={`/properties?type=${c.key}`} data-testid={`category-${c.key}`}
              className="fl-card group relative rounded-2xl overflow-hidden border border-slate-200 aspect-[4/5] bg-white">
              <img src={c.img} alt={c.ar} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/85 via-emerald-950/10 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-4">
                <div className="text-white font-bold text-sm sm:text-base">{lang === "ar" ? c.ar : c.en}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LATEST */}
      {latest.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-emerald-950">{t.home.latest}</h2>
            <Link to="/properties" className="text-sm text-emerald-900 hover:text-amber-700 font-semibold inline-flex items-center gap-1">
              {t.home.seeAll} <Arrow className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {latest.slice(0, 8).map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </section>
      )}

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-emerald-950 mb-6">{t.home.featured}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.slice(0, 6).map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </section>
      )}

      {/* WHY */}
      <section className="fl-mint-panel">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-emerald-950 mb-10 text-center">{t.home.why}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[Handshake, MapPinned, Landmark, Sparkles].map((Icon, i) => (
              <div key={i} className="bg-white rounded-2xl border border-emerald-900/10 p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 grid place-items-center mx-auto mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-bold text-emerald-950">{t.home.whyList[i]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-white border border-emerald-900/10 rounded-3xl p-8 sm:p-14 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <h3 className="text-3xl font-extrabold text-emerald-950">{t.home.finalCta}</h3>
            <p className="mt-2 text-slate-700">{t.home.finalCtaSub}</p>
            <Button data-testid="final-cta" onClick={() => nav("/sell")} className="mt-6 bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold h-12 px-6">
              {t.home.start}
            </Button>
          </div>
          <div className="hidden md:block relative aspect-[5/4] rounded-2xl overflow-hidden border border-emerald-900/10 shadow-[0_30px_60px_-30px_rgba(6,44,34,.35)]">
            <img src="/img/hero-3.png" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/30 via-transparent to-transparent" />
          </div>
        </div>
      </section>
    </div>
  );
}
